'use client';

import { useState } from 'react';
import { Play } from 'lucide-react';
import { cn } from '../utils/cn';

interface StaticMediaThumbProps {
  /** Preferred small thumbnail URL (e.g. Generation.thumbnailUrl). */
  thumbnailUrl?: string | null;
  /** Full original URL — used as fallback when no thumbnail exists yet. */
  fallbackUrl?: string | null;
  /** When true, overlays a play icon to hint that the source is a video. */
  isVideo?: boolean;
  alt?: string;
  className?: string;
  /** Layout mode: `cover` (absolute fill) or `flow` (block, h-auto for masonry). */
  fit?: 'cover' | 'flow';
  iconSize?: 'sm' | 'md' | 'lg';
  /** Show a soft pulse skeleton until the image decodes. */
  showSkeleton?: boolean;
}

/**
 * Static, lightweight grid tile for feed/history.
 *
 * Renders a single `<img>` (the small WebP thumbnail produced by the queue
 * worker), never a `<video>`, so scrolling through dozens of generations
 * costs ~50KB per tile instead of multi-MB originals or autoplay video.
 *
 * Falls back to `fallbackUrl` for legacy rows where the worker hadn't run
 * the thumbnail step yet.
 */
export function StaticMediaThumb({
  thumbnailUrl,
  fallbackUrl,
  isVideo = false,
  alt,
  className,
  fit = 'cover',
  iconSize = 'md',
  showSkeleton = true,
}: StaticMediaThumbProps) {
  const [loaded, setLoaded] = useState(false);
  const src = thumbnailUrl || fallbackUrl;
  if (!src) return null;

  const iconSizes = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
  };

  const imgClass =
    fit === 'cover'
      ? `absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`
      : `block h-auto w-full transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`;

  return (
    <div className={cn(fit === 'cover' ? 'relative h-full w-full' : 'relative w-full', className)}>
      {showSkeleton && !loaded && (
        <div
          className={cn(
            fit === 'cover' ? 'absolute inset-0' : 'absolute inset-0',
            'animate-pulse bg-white/[0.04]',
          )}
        />
      )}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={alt || ''}
        loading="lazy"
        decoding="async"
        draggable={false}
        onLoad={() => setLoaded(true)}
        className={imgClass}
      />
      {isVideo && loaded && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm">
            <Play className={cn(iconSizes[iconSize], 'text-white')} />
          </div>
        </div>
      )}
    </div>
  );
}
